import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CommunityFeedFilters } from '../../services/communityService';

/** "Zeitraum" presets (days back from today) plus a custom from–to range. '' = all time. */
export type FeedPeriod = '' | '7' | '30' | '90' | 'custom';
export type FeedSort = 'newest' | 'oldest';

/** The feed filters as they live in the URL (shareable, survive reload). */
export interface FeedFilterState {
  period: FeedPeriod;
  /** YYYY-MM-DD, only with period = 'custom'. */
  from: string;
  /** YYYY-MM-DD, only with period = 'custom'. */
  to: string;
  aquarium: string;
  tag: string;
  mine: boolean;
  q: string;
  sort: FeedSort;
  page: number;
}

const PERIODS: readonly FeedPeriod[] = ['7', '30', '90', 'custom'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isValidDate(value: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

/** Reads the filter state from URL search params; unknown/invalid values fall back to the defaults. */
export function parseFeedFilterParams(params: URLSearchParams): FeedFilterState {
  const period = params.get('period') ?? '';
  const page = Number.parseInt(params.get('page') ?? '1', 10);
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  return {
    period: (PERIODS as readonly string[]).includes(period) ? (period as FeedPeriod) : '',
    from: isValidDate(from) ? from : '',
    to: isValidDate(to) ? to : '',
    aquarium: (params.get('aquarium') ?? '').slice(0, 100),
    tag: (params.get('tag') ?? '').slice(0, 50),
    mine: params.get('mine') === '1' || params.get('mine') === 'true',
    q: (params.get('q') ?? '').slice(0, 200),
    sort: params.get('sort') === 'oldest' ? 'oldest' : 'newest',
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

/** The URL search params of a filter state — defaults are left out so a clean feed has a clean URL. */
export function toFeedFilterParams(state: FeedFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.period) params.set('period', state.period);
  if (state.period === 'custom') {
    if (state.from) params.set('from', state.from);
    if (state.to) params.set('to', state.to);
  }
  if (state.aquarium.trim()) params.set('aquarium', state.aquarium.trim());
  if (state.tag) params.set('tag', state.tag);
  if (state.mine) params.set('mine', '1');
  if (state.q.trim()) params.set('q', state.q.trim());
  if (state.sort !== 'newest') params.set('sort', state.sort);
  if (state.page > 1) params.set('page', String(state.page));
  return params;
}

/** True when the custom range is reversed (from after to). */
export function isFeedRangeInvalid(state: FeedFilterState): boolean {
  return state.period === 'custom' && Boolean(state.from && state.to) && state.from > state.to;
}

function localDayStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseLocalDate(value: string): Date {
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

/**
 * The API filters of a filter state. Dates are the user's LOCAL days, sent as
 * exact ISO instants: a preset starts at local midnight N days ago (stable for
 * the whole day, so the query key does not change on every render), a custom
 * range covers the whole local "to" day. A reversed range is not sent.
 */
export function toApiFilters(state: FeedFilterState, now: Date = new Date()): CommunityFeedFilters {
  const filters: CommunityFeedFilters = {};
  if (state.period === '7' || state.period === '30' || state.period === '90') {
    const start = localDayStart(now);
    start.setDate(start.getDate() - Number(state.period));
    filters.from = start.toISOString();
  } else if (state.period === 'custom' && !isFeedRangeInvalid(state)) {
    if (state.from) filters.from = parseLocalDate(state.from).toISOString();
    if (state.to) {
      const end = parseLocalDate(state.to);
      end.setDate(end.getDate() + 1);
      filters.to = new Date(end.getTime() - 1).toISOString();
    }
  }
  if (state.aquarium.trim()) filters.aquariumName = state.aquarium.trim();
  if (state.tag) filters.tag = state.tag;
  if (state.mine) filters.mine = true;
  if (state.q.trim()) filters.q = state.q.trim();
  if (state.sort !== 'newest') filters.sort = state.sort;
  return filters;
}

/** Number of active filters (sort and paging do not count). */
export function countActiveFeedFilters(state: FeedFilterState): number {
  return [
    state.period !== '',
    state.aquarium.trim() !== '',
    state.tag !== '',
    state.mine,
    state.q.trim() !== '',
  ].filter(Boolean).length;
}

/**
 * Community feed filters backed by the URL search params. Every filter change
 * goes back to page 1 and replaces the history entry (no Back-button trail
 * per keystroke); paging pushes a new entry.
 */
export function useCommunityFeedFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseFeedFilterParams(searchParams), [searchParams]);

  const update = useCallback(
    (patch: Partial<Omit<FeedFilterState, 'page'>>) => {
      setSearchParams((prev) => toFeedFilterParams({ ...parseFeedFilterParams(prev), ...patch, page: 1 }), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (page: number) => {
      setSearchParams((prev) => toFeedFilterParams({ ...parseFeedFilterParams(prev), page }));
    },
    [setSearchParams],
  );

  const reset = useCallback(() => {
    // Keep the chosen sort order; clear every filter.
    setSearchParams((prev) => {
      const { sort } = parseFeedFilterParams(prev);
      return toFeedFilterParams({ ...parseFeedFilterParams(new URLSearchParams()), sort });
    }, { replace: true });
  }, [setSearchParams]);

  return { state, update, setPage, reset };
}
